/**
 * ETA Calculator Module
 * Calculate Estimated Time to Finish using MacCready theory with final glide optimization
 * Based on extend_spectate_full_features.py calculate_etf() lines 2227-2533
 */

/**
 * Calculate Estimated Time to Finish (ETF)
 *
 * @param {number} currentAlt_m - Current TEC altitude in meters
 * @param {number} finishAlt_m - Finish altitude in meters
 * @param {number} remainingDistance_km - Remaining task distance in km
 * @param {number} mcSetting_ms - MacCready setting in m/s
 * @param {number} netto_ms - Netto setting in m/s (air mass movement)
 * @param {object} polarManager - Polar manager instance
 * @param {string} gliderType - Glider type name
 * @param {number} wingLoading - Current wing loading in kg/m²
 * @param {number} vne_kmh - VNE (Never Exceed speed) in km/h
 * @param {string} aircraftId - Optional aircraft identifier for debug logging
 * @returns {object} { etfTotal_min, etfCruise_min, etfClimb_min, finalGlideSpeed_kmh, finishAltDelta_m, debugData }
 */
export function calculateETA(
  currentAlt_m,
  finishAlt_m,
  remainingDistance_km,
  mcSetting_ms,
  netto_ms,
  polarManager,
  gliderType,
  wingLoading,
  vne_kmh = 280,
  aircraftId = null
) {
  const debug = aircraftId !== null; // Enable debug if aircraftId is provided
  const debugPrefix = aircraftId ? `[${aircraftId} ETF]` : 'ETA:';
  const debugData = {};

  // Validate inputs
  if (remainingDistance_km <= 0.01) {
    if (debug) console.log(`${debugPrefix} Remaining distance too small: ${remainingDistance_km.toFixed(3)} km`);
    return {
      etfTotal_min: 0,
      etfCruise_min: 0,
      etfClimb_min: 0,
      finalGlideSpeed_kmh: 0,
      finishAltDelta_m: 0,
      debugData: {}
    };
  }

  // Get polar coefficients with wing loading adjustment
  const coeffs = polarManager.getPolarCoefficients(gliderType, wingLoading);
  if (!coeffs) {
    if (debug) console.log(`${debugPrefix} No polar data for ${gliderType}`);
    return {
      etfTotal_min: 0,
      etfCruise_min: 0,
      etfClimb_min: 0,
      finalGlideSpeed_kmh: 0,
      finishAltDelta_m: null,
      debugData: {}
    };
  }

  let [a, b, c] = coeffs;
  const originalC = c; // Store original before netto adjustment

  // Apply netto adjustment to polar: shift sink rates by netto
  // Netto affects cruise performance by changing the effective sink rate
  // Since sink values are negative, we ADD netto to make them less negative (better performance)
  c = c + netto_ms;

  if (debug) {
    console.log(`${debugPrefix} ========== ETF CALCULATION ==========`);
    console.log(`${debugPrefix} INPUT PARAMETERS:`);
    console.log(`${debugPrefix}   Glider Type: ${gliderType}`);
    console.log(`${debugPrefix}   Current TEC Alt: ${currentAlt_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Finish Alt: ${finishAlt_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Available Alt: ${(currentAlt_m - finishAlt_m).toFixed(1)}m`);
    console.log(`${debugPrefix}   Remaining Distance: ${remainingDistance_km.toFixed(2)}km`);
    console.log(`${debugPrefix}   MC Setting: ${mcSetting_ms.toFixed(2)}m/s (${(mcSetting_ms * 3.6).toFixed(1)}km/h climb rate)`);
    console.log(`${debugPrefix}   Netto: ${netto_ms.toFixed(2)}m/s`);
    console.log(`${debugPrefix}   Wing Loading: ${wingLoading.toFixed(1)}kg/m²`);
    console.log(`${debugPrefix}   VNE: ${vne_kmh.toFixed(0)}km/h`);
    console.log(`${debugPrefix} POLAR COEFFICIENTS:`);
    console.log(`${debugPrefix}   Original: a=${a.toFixed(6)}, b=${b.toFixed(6)}, c=${originalC.toFixed(6)}`);
    console.log(`${debugPrefix}   Netto-adjusted: a=${a.toFixed(6)}, b=${b.toFixed(6)}, c=${c.toFixed(6)}`);
  }

  // Find MacCready speed using tangent to polar from MC point
  // This is the speed-to-fly that optimizes travel time for the given MC setting
  let bestSpeed_kmh = 150; // Default fallback
  let optimalSink_ms = -2.0;

  // MacCready theory: Find speed where line from (0, -MC) is tangent to polar curve
  // For polar sink = a*v² + b*v + c, and MC line y = -MC
  // Tangent condition: derivative of polar = slope of line to (v, sink(v))
  // 2*a*v + b = (a*v² + b*v + c + MC) / v
  // Solving: a*v² - MC*v = 0, so v = MC/a (if MC > 0)

  if (mcSetting_ms > 0 && a !== 0) {
    // Calculate MC speed analytically
    // The tangent from (0, -MC) to the parabola sink = a*v² + b*v + c
    // Discriminant method: (2*a*v + b)*v = a*v² + b*v + c + MC
    // Simplifies to: a*v² - MC = 0, so v_mc = sqrt(MC / a)
    // But proper MacCready formula involves the tangent line...

    // Use the correct tangent formula: Find v where line from (0, -MC) touches polar
    // dy/dv = (sink(v) + MC) / v  (slope from origin to point on polar)
    // Must equal 2*a*v + b (derivative of polar)
    // So: 2*a*v + b = (a*v² + b*v + c + MC) / v
    // Multiply by v: 2*a*v² + b*v = a*v² + b*v + c + MC
    // Simplify: a*v² = c + MC
    // v = sqrt((c + MC) / a)  -- BUT c is negative sink at v=0...

    // Actually, the standard formula is:
    // v_mc = sqrt(MC / a_adjusted) where a_adjusted accounts for the polar shape
    // Let's use a simpler approximation: find minimum of (time) = (sink + MC) / v
    // This is equivalent to finding the tangent from -MC

    // For parabola a*v² + b*v + c, tangent from (0, -MC):
    // Solve: 2*a*v + b = (a*v² + b*v + c - (-MC)) / v
    // 2*a*v² + b*v = a*v² + b*v + c + MC
    // a*v² = c + MC
    // v = sqrt((c + MC) / a) if a < 0 (sink increases with speed²)

    if (a < 0) {  // Typical for glider polars
      const term = (c - mcSetting_ms) / a;  // Note: c is negative, -c is positive
      if (term > 0) {
        const v_mc_ms = Math.sqrt(term);
        bestSpeed_kmh = v_mc_ms * 3.6;
        optimalSink_ms = a * v_mc_ms * v_mc_ms + b * v_mc_ms + c;
      } else {
        // The tangent formula has no valid solution - this happens when:
        // (c - mc) / a <= 0, which means (c - mc) > 0 since a < 0
        // i.e., the netto-adjusted sink (c) is greater than MC climb rate
        // This occurs in very strong lift where the glider is climbing at all speeds,
        // OR when MC setting produces a theoretical speed exceeding what the polar can represent.
        // In either case, fly at VNE - get there as fast as possible.
        if (debug) console.log(`${debugPrefix} MC tangent formula invalid (netto-adjusted c=${c.toFixed(2)}, MC=${mcSetting_ms.toFixed(2)}), using VNE`);
        const vne_ms = vne_kmh / 3.6;
        bestSpeed_kmh = vne_kmh;
        optimalSink_ms = a * vne_ms * vne_ms + b * vne_ms + c;
      }
    }
  } else {
    // No MC setting, use minimum sink speed
    if (a !== 0) {
      const v_min_ms = -b / (2 * a);
      bestSpeed_kmh = v_min_ms * 3.6;
      optimalSink_ms = a * v_min_ms * v_min_ms + b * v_min_ms + c;
    }
  }

  // Validate speed range - cap at VNE instead of falling back to min sink
  if (bestSpeed_kmh < 50) {
    if (debug) console.log(`${debugPrefix} Speed ${bestSpeed_kmh.toFixed(1)}km/h too low, using min sink speed`);
    const v_min_ms = -b / (2 * a);
    bestSpeed_kmh = v_min_ms * 3.6;
    optimalSink_ms = a * v_min_ms * v_min_ms + b * v_min_ms + c;
  } else if (bestSpeed_kmh > vne_kmh) {
    // Speed exceeds VNE - cap at VNE (don't fall back to min sink!)
    if (debug) console.log(`${debugPrefix} Speed ${bestSpeed_kmh.toFixed(1)}km/h exceeds VNE, capping at ${vne_kmh}km/h`);
    bestSpeed_kmh = vne_kmh;
    const vne_ms = vne_kmh / 3.6;
    optimalSink_ms = a * vne_ms * vne_ms + b * vne_ms + c;
  }

  if (debug) {
    console.log(`${debugPrefix} MACCREADY SPEED:`);
    console.log(`${debugPrefix}   MC Speed: ${bestSpeed_kmh.toFixed(1)}km/h`);
    console.log(`${debugPrefix}   Sink at MC Speed: ${optimalSink_ms.toFixed(3)}m/s`);
    const mcLD = Math.abs(optimalSink_ms) > 0.001 ? (bestSpeed_kmh / 3.6) / Math.abs(optimalSink_ms) : 0;
    console.log(`${debugPrefix}   L/D at MC Speed: ${mcLD.toFixed(1)}`);
  }

  // ===== FINAL GLIDE OPTIMIZATION =====
  const availableAltitude_m = currentAlt_m - finishAlt_m;

  debugData.finishAltitude = finishAlt_m;
  debugData.remainingDistance = remainingDistance_km;
  debugData.tecAltitude = currentAlt_m;
  debugData.availableAltitude = availableAltitude_m;

  // Calculate required glide ratio
  let requiredLD = null;
  if (remainingDistance_km > 0 && availableAltitude_m > 0) {
    requiredLD = (remainingDistance_km * 1000.0) / availableAltitude_m;
    debugData.requiredLD = requiredLD;
  } else {
    debugData.requiredLD = null;
  }

  if (debug) {
    console.log(`${debugPrefix} GLIDE RATIOS:`);
    console.log(`${debugPrefix}   Required L/D: ${requiredLD ? requiredLD.toFixed(1) : 'N/A'}`);
  }

  // Calculate altitude needed at MC speed
  const mcSpeed_ms = bestSpeed_kmh / 3.6;
  const mcSink_positive = Math.max(0.0, -optimalSink_ms);
  const mcTime_h = remainingDistance_km / bestSpeed_kmh;
  const altitudeNeededAtMC = mcSink_positive * mcTime_h * 3600; // meters

  debugData.mcSpeed = bestSpeed_kmh;
  if (mcSink_positive > 1e-6) {
    debugData.mcLD = mcSpeed_ms / mcSink_positive;
  } else {
    debugData.mcLD = null;
  }
  debugData.altitudeNeededMC = altitudeNeededAtMC;

  // Calculate excess altitude
  const excessAltitude_m = availableAltitude_m - altitudeNeededAtMC;
  debugData.altitudeExcessMC = excessAltitude_m;

  if (debug) {
    console.log(`${debugPrefix} ALTITUDE ANALYSIS:`);
    console.log(`${debugPrefix}   Available: ${availableAltitude_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Needed at MC speed: ${altitudeNeededAtMC.toFixed(1)}m`);
    console.log(`${debugPrefix}   Excess: ${excessAltitude_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Time at MC speed: ${(mcTime_h * 60).toFixed(2)}min`);
  }

  // If we have excess altitude, we can speed up beyond MC speed
  if (excessAltitude_m > 1 && remainingDistance_km > 0) {
    if (debug) {
      console.log(`${debugPrefix} FINAL GLIDE OPTIMIZATION:`);
      console.log(`${debugPrefix}   Excess altitude available - can speed up beyond MC!`);
    }

    const distance_m = remainingDistance_km * 1000.0;

    // Function to calculate altitude lost at a given speed
    const altitudeLost = (speed_ms) => {
      if (speed_ms <= 0) return Infinity;
      const sink = a * speed_ms * speed_ms + b * speed_ms + c;
      const sink_positive = Math.max(0.0, -sink);
      return sink_positive * (distance_m / speed_ms);
    };

    const vne_ms = vne_kmh / 3.6;
    const altLossMC = altitudeLost(mcSpeed_ms);
    const altLossVNE = altitudeLost(vne_ms);

    if (debug) {
      console.log(`${debugPrefix}   Alt loss @ MC (${bestSpeed_kmh.toFixed(1)}km/h): ${altLossMC.toFixed(1)}m`);
      console.log(`${debugPrefix}   Alt loss @ VNE (${vne_kmh.toFixed(1)}km/h): ${altLossVNE.toFixed(1)}m`);
    }

    if (altLossMC <= availableAltitude_m + 0.5) {
      let targetSpeed_ms = mcSpeed_ms;

      if (altLossVNE <= availableAltitude_m || altLossVNE <= 1e-3) {
        targetSpeed_ms = vne_ms;
        if (debug) console.log(`ETA: Can use VNE - altitude sufficient`);
      } else {
        // Binary search for optimal speed between MC and VNE
        let low = mcSpeed_ms;
        let high = vne_ms;

        for (let i = 0; i < 40; i++) {
          const mid = (low + high) / 2.0;
          const loss = altitudeLost(mid);

          if (loss <= availableAltitude_m) {
            targetSpeed_ms = mid;
            low = mid;
          } else {
            high = mid;
          }
        }

        if (debug) {
          const targetSpeed_kmh = targetSpeed_ms * 3.6;
          console.log(`${debugPrefix}   Optimized speed: ${targetSpeed_kmh.toFixed(1)}km/h`);
        }
      }

      // Update to faster speed if it's meaningfully faster
      if (targetSpeed_ms > mcSpeed_ms + 0.05) {
        bestSpeed_kmh = targetSpeed_ms * 3.6;

        // Cap at VNE
        if (bestSpeed_kmh > vne_kmh) {
          bestSpeed_kmh = vne_kmh;
          targetSpeed_ms = vne_ms;
        }

        optimalSink_ms = a * targetSpeed_ms * targetSpeed_ms + b * targetSpeed_ms + c;

        if (debug) {
          console.log(`${debugPrefix}   Final optimized speed: ${bestSpeed_kmh.toFixed(1)}km/h`);
        }
      }
    }
  }

  // Ensure we don't exceed VNE
  if (bestSpeed_kmh > vne_kmh) {
    bestSpeed_kmh = vne_kmh;
    const vne_ms = vne_kmh / 3.6;
    optimalSink_ms = a * vne_ms * vne_ms + b * vne_ms + c;
    if (debug) console.log(`${debugPrefix} Capped at VNE: ${vne_kmh}km/h`);
  }

  debugData.finalGlideSpeed = bestSpeed_kmh;

  // Calculate ETF components
  const cruiseTime_h = remainingDistance_km / bestSpeed_kmh;
  const optimalSink_positive = Math.max(0.0, -optimalSink_ms);
  const cruiseDuration_s = cruiseTime_h * 3600;
  const altitudeLostCruise_m = optimalSink_positive * cruiseDuration_s;

  const altDeficit_m = finishAlt_m - currentAlt_m;
  const totalAltitudeNeeded_m = altDeficit_m + altitudeLostCruise_m;

  debugData.altitudeExcessFinal = availableAltitude_m - altitudeLostCruise_m;

  // Calculate climb time needed
  let climbTime_h = 0.0;
  if (totalAltitudeNeeded_m > 0 && mcSetting_ms > 0) {
    const climbTime_s = totalAltitudeNeeded_m / mcSetting_ms;
    climbTime_h = climbTime_s / 3600;
  }

  const totalTime_h = cruiseTime_h + climbTime_h;

  if (debug) {
    console.log(`${debugPrefix} FINAL CALCULATION RESULTS:`);
    console.log(`${debugPrefix}   Final Speed: ${bestSpeed_kmh.toFixed(1)}km/h`);
    console.log(`${debugPrefix}   Sink at Final Speed: ${optimalSink_ms.toFixed(3)}m/s`);
    const finalLD = Math.abs(optimalSink_ms) > 0.001 ? (bestSpeed_kmh / 3.6) / Math.abs(optimalSink_ms) : 0;
    console.log(`${debugPrefix}   L/D at Final Speed: ${finalLD.toFixed(1)}`);
    console.log(`${debugPrefix}   Altitude Lost in Cruise: ${altitudeLostCruise_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Altitude Deficit: ${altDeficit_m.toFixed(1)}m`);
    console.log(`${debugPrefix}   Total Altitude Needed: ${totalAltitudeNeeded_m.toFixed(1)}m`);
    console.log(`${debugPrefix} TIME BREAKDOWN:`);
    console.log(`${debugPrefix}   Cruise Time: ${(cruiseTime_h * 60).toFixed(2)}min`);
    console.log(`${debugPrefix}   Climb Time: ${(climbTime_h * 60).toFixed(2)}min`);
    console.log(`${debugPrefix}   TOTAL ETF: ${(totalTime_h * 60).toFixed(2)}min`);
    console.log(`${debugPrefix} ========================================`);
  }

  return {
    etfTotal_min: totalTime_h * 60,
    etfCruise_min: cruiseTime_h * 60,
    etfClimb_min: climbTime_h * 60,
    finalGlideSpeed_kmh: bestSpeed_kmh,
    finishAltDelta_m: excessAltitude_m,
    debugData
  };
}

/**
 * Estimate how much distance can still be flown in the available AAT time.
 * Uses the same MC/polar model as ETF, but solves for distance instead of time.
 *
 * @returns {object|null} { remainingDistance_km, xcSpeed_kmh, cruiseSpeed_kmh, altitudeEquivalentDistance_km, debugData }
 */
export function calculateDistanceForTime(
  currentAlt_m,
  finishAlt_m,
  remainingTime_min,
  mcSetting_ms,
  netto_ms,
  polarManager,
  gliderType,
  wingLoading,
  vne_kmh = 280,
  aircraftId = null
) {
  const debug = aircraftId !== null;
  const debugPrefix = aircraftId ? `[${aircraftId} AAT]` : 'AAT:';

  if (!Number.isFinite(remainingTime_min) || remainingTime_min <= 0) {
    return {
      remainingDistance_km: 0,
      xcSpeed_kmh: 0,
      cruiseSpeed_kmh: 0,
      altitudeEquivalentDistance_km: 0,
      debugData: {}
    };
  }

  if (!Number.isFinite(mcSetting_ms) || mcSetting_ms <= 0) {
    if (debug) console.log(`${debugPrefix} Invalid MC setting: ${mcSetting_ms}`);
    return null;
  }

  const coeffs = polarManager.getPolarCoefficients(gliderType, wingLoading);
  if (!coeffs) {
    if (debug) console.log(`${debugPrefix} No polar data for ${gliderType}`);
    return null;
  }

  let [a, b, c] = coeffs;
  c = c + netto_ms;

  let bestCruiseSpeed_kmh = null;
  let bestXcSpeed_kmh = -Infinity;
  let bestSink_ms = null;

  for (let speedKmh = 80; speedKmh <= vne_kmh; speedKmh += 2) {
    const speed_ms = speedKmh / 3.6;
    const sink_ms = a * speed_ms * speed_ms + b * speed_ms + c;
    const sinkAbs_ms = Math.max(0.05, Math.abs(sink_ms));
    const xcSpeed_ms = speed_ms / (1 + (sinkAbs_ms / mcSetting_ms));
    const xcSpeed_kmh = xcSpeed_ms * 3.6;

    if (xcSpeed_kmh > bestXcSpeed_kmh) {
      bestCruiseSpeed_kmh = speedKmh;
      bestXcSpeed_kmh = xcSpeed_kmh;
      bestSink_ms = sink_ms;
    }
  }

  if (
    !Number.isFinite(bestCruiseSpeed_kmh) ||
    !Number.isFinite(bestXcSpeed_kmh) ||
    !Number.isFinite(bestSink_ms)
  ) {
    return null;
  }

  const deltaAlt_km = (currentAlt_m - finishAlt_m) / 1000.0;
  const sinkAbs_kmh = Math.max(0.1, Math.abs(bestSink_ms * 3.6));
  const glideRatio = bestCruiseSpeed_kmh / sinkAbs_kmh;
  const equivalentTime_h = deltaAlt_km / sinkAbs_kmh;
  const altitudeEquivalentDistance_km =
    (deltaAlt_km * glideRatio) - (equivalentTime_h * bestXcSpeed_kmh);
  const timeDistance_km = bestXcSpeed_kmh * (remainingTime_min / 60);
  const remainingDistance_km = Math.max(0, timeDistance_km + altitudeEquivalentDistance_km);

  const debugData = {
    remainingTime_min,
    cruiseSpeed_kmh: bestCruiseSpeed_kmh,
    xcSpeed_kmh: bestXcSpeed_kmh,
    deltaAlt_km,
    glideRatio,
    altitudeEquivalentDistance_km
  };

  if (debug) {
    console.log(`${debugPrefix} Distance from remaining time`, {
      remainingTime_min: remainingTime_min.toFixed(2),
      cruiseSpeed_kmh: bestCruiseSpeed_kmh.toFixed(1),
      xcSpeed_kmh: bestXcSpeed_kmh.toFixed(1),
      deltaAlt_km: deltaAlt_km.toFixed(3),
      altitudeEquivalentDistance_km: altitudeEquivalentDistance_km.toFixed(2),
      remainingDistance_km: remainingDistance_km.toFixed(2)
    });
  }

  return {
    remainingDistance_km,
    xcSpeed_kmh: bestXcSpeed_kmh,
    cruiseSpeed_kmh: bestCruiseSpeed_kmh,
    altitudeEquivalentDistance_km,
    debugData
  };
}

/**
 * Format time in minutes to MM:SS string
 * @param {number} minutes - Time in minutes
 * @returns {string} Formatted time string
 */
export function formatTimeMMSS(minutes) {
  if (!isFinite(minutes) || minutes < 0) return '--:--';

  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
